import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import * as XLSX from 'xlsx';
import { Grid, GridColumn, GridSortChangeEvent, GridFilterChangeEvent, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { SortDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Switch } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import {
  FaFileExcel,
  FaPlusCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaFileInvoice,
  FaStickyNote,
  FaPaperPlane,
  FaEnvelope,
} from "react-icons/fa";

import "./Grids.css";
import { DateFormatter } from "../../data/helper";
import { quotesGrid, dropDown } from "../../data/interfaces";
import { FormMultiSelect } from "../FormComps/FormComponents";
import { MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import { getEmployeeDropdown } from "../../data/employeeData";
import {
  getQuotesWithPagination,
  getClientsForQuotesFilterDropdown,
  getSitesForQuotesFilterDropdown,
  getWorkTypesForQuotesFilterDropdown,
  getAssignedToForQuotesFilterDropdown,
  getQuotesForQuotesFilterDropdown,
} from "../../data/quoteData";
import { useSessionState } from "../../helpers/useSessionState";

const formatBool = (val: boolean | null | undefined): string =>
  val === true ? "YES" : val === false ? "NO" : "";

// Custom filter cell for client column with dropdown
const ClientFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState(props.data || []);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const clientsData = await getClientsForQuotesFilterDropdown(filter);
      const clientNames = clientsData.map((c: any) => c.text).sort();
      setFilteredData(clientNames);
    } catch (error) {
      console.error('Error loading filtered clients:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by client..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for site column with dropdown
const SiteFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState(props.data || []);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const sitesData = await getSitesForQuotesFilterDropdown(filter);
      const siteNames = sitesData.map((s: any) => s.text).sort();
      setFilteredData(siteNames);
    } catch (error) {
      console.error('Error loading filtered sites:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by site..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for quote ref column with dropdown
const QuoteRefFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState(props.data || []);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const quotesData = await getQuotesForQuotesFilterDropdown(filter);
      const quoteRefs = quotesData.map((q: any) => q.text).sort();
      setFilteredData(quoteRefs);
    } catch (error) {
      console.error('Error loading filtered quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by quote..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for worktype column with dropdown
const WorkTypeFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState(props.data || []);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const workTypesData = await getWorkTypesForQuotesFilterDropdown(filter);
      const workTypeNames = workTypesData.map((w: any) => w.text).sort();
      setFilteredData(workTypeNames);
    } catch (error) {
      console.error('Error loading filtered work types:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by work type..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for Assigned To column with dropdown
const AssignedToFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const assignedToData = await getAssignedToForQuotesFilterDropdown(filter);
      const assignedToNames = assignedToData.map((a: any) => a.text).sort();
      setFilteredData(assignedToNames);
    } catch (error) {
      console.error('Error loading assigned to:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by assigned to..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

function QuotesGrid(props: any) {
  const dateFormatter = new DateFormatter();
  
  // State for Kendo Grid
  const [data, setData] = useState<quotesGrid[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Pagination state (page size persists, page position resets on re-entry)
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useSessionState("quotes.take", 20);

  // Sorting state
  const [sort, setSort] = useSessionState<SortDescriptor[]>("quotes.sort", []);

  // Filtering state
  const [filter, setFilter] = useSessionState<CompositeFilterDescriptor | undefined>("quotes.filter", undefined);

  // Toggle switches
  const [showRejected, setShowRejected] = useSessionState("quotes.showRejected", false);
  const [showAccepted, setShowAccepted] = useSessionState("quotes.showAccepted", false);
  
  // Month and employee filters
  const [monthSelect] = useState<dropDown[]>([
    { value: 0, text: "" },
    { value: 1, text: "January" },
    { value: 2, text: "February" },
    { value: 3, text: "March" },
    { value: 4, text: "April" },
    { value: 5, text: "May" },
    { value: 6, text: "June" },
    { value: 7, text: "July" },
    { value: 8, text: "August" },
    { value: 9, text: "September" },
    { value: 10, text: "October" },
    { value: 11, text: "November" },
    { value: 12, text: "December" },
  ]);
  
  // Sent status filter — null shows all quotes
  const [sentSelect] = useState<{ value: string; text: string }[]>([
    { value: "", text: "All" },
    { value: "true", text: "Sent" },
    { value: "false", text: "Not Sent" },
  ]);

  const [currentMonth, setCurrentMonth] = useSessionState<number | null>("quotes.currentMonth", null);
  const [sentFilter, setSentFilter] = useSessionState<boolean | null>("quotes.sentFilter", null);
  const [employeeFilter, setEmployeeFilter] = useSessionState<number[]>("quotes.employeeFilter", []);
  const [employeeData, setEmployeeData] = useState<dropDown[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useSessionState<dropDown[]>("quotes.selectedEmployees", []);

  const handleShowRejected = (event: any) => {
    setShowRejected(event.target.value);
    if (props.onShowRejected) {
      props.onShowRejected(event.target.value);
    }
  };

  const handleShowAccepted = (event: any) => {
    setShowAccepted(event.target.value);
    if (props.onShowAccepted) {
      props.onShowAccepted(event.target.value);
    }
  };

  const handleMonthCreated = (event: any) => {
    const month = +event.target.value;
    setCurrentMonth(month > 0 ? month : null);
  };

  const handleSentFilter = (event: any) => {
    const value = event.target.value;
    setSentFilter(value === "" ? null : value === "true");
    setSkip(0);
  };

  const handleAddFilteredEmployee = (event: MultiSelectChangeEvent) => {
    const selected = event.value as dropDown[];
    setSelectedEmployees(selected);
    setEmployeeFilter(selected.map((employee) => employee.value));
  };

  const handleApplyEmployeeFilter = () => {
    if (props.onQuoteCreatedBy) {
      props.onQuoteCreatedBy(employeeFilter);
    }
  };

  // Load data function
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getQuotesWithPagination({
        skip,
        take,
        sort: sort.map(s => ({ field: s.field!, dir: s.dir! })),
        filter,
        showRejected,
        showWon: showAccepted,
        month: currentMonth,
        filterIds: employeeFilter,
        isSent: sentFilter,
      });
      
      setData(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  }, [skip, take, sort, filter, showRejected, showAccepted, currentMonth, sentFilter, employeeFilter]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData, props.refreshKey]);

  // Load employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      const data = await getEmployeeDropdown('');
      setEmployeeData(data);
    };
    fetchEmployeeData();
  }, []);

  // Handle page change
  const handlePageChange = (event: GridPageChangeEvent) => {
    setSkip(event.page.skip);
    setTake(event.page.take);
  };

  // Handle sort change
  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
    setSkip(0); // Reset to first page when sorting changes
  };

  // Handle filter change
  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter);
  };

  const exportToExcel = () => {
    const exportList: any[] = props.onExportData ?? [];
    
    const exportData = data.map(base => {
      const full = exportList.find(e => e.quoteId === base.quoteId) || {};
      return {
        ...base,
        note1Details: full.note1Details || "",
        note2Details: full.note2Details || "",
        note3Details: full.note3Details || "",
        note1Completed: formatBool(full.note1Completed),
        note2Completed: formatBool(full.note2Completed),
        note3Completed: formatBool(full.note3Completed),
      };
    });

    const headers = [
      "noteCount", "quoteId", "ref", "refFormatted", "client", "site", "worktype", 
      "priority", "priorityId", "followupDate", "createdDate", "wonDate", "lostDate", "lostReason",
      "description", "cost", "createdBy", "assignedTo", "quoteDoc", "qualsDescription", 
      "quoteTemplateId", "shortDescription", "worksDescription", "value", "isSent", 
      "note1Details", "note1Completed", "note2Details", "note2Completed", 
      "note3Details", "note3Completed"
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData, { header: headers });

    XLSX.utils.book_append_sheet(wb, ws, "Quotes");
    const dateStr = dateFormatter.format(new Date().toDateString());
    XLSX.writeFile(wb, `quotes_${dateStr}.xlsx`);
  };

  // Row render with conditional formatting
  const rowRender = (tr: React.ReactElement, rowProps: any) => {
    const row = rowProps.dataItem;
    
    const bgColor = () => {
      if (row.lostDate) {
        return "rgba(235, 199, 199, 0.5)";
      } else if (row.wonDate) {
        return "#d7edd7";
      } else {
        return "rgb(245 249 251)";
      }
    };

    const textColor = row.lostDate ? "rgb(137 5 5)" : "#1e2a4e";

    return React.cloneElement(tr, {
      ...tr.props,
      style: {
        ...tr.props.style,
        backgroundColor: bgColor(),
        color: textColor,
        cursor: 'pointer',
        border: "1px solid rgb(216 229 237)",
      },
      onDoubleClick: () => {
        if (props.onEditToggle) {
          props.onEditToggle(rowProps.dataItem.quoteId);
        }
      },
    }, tr.props.children);
  };

  // Custom cell renderer for quote ref with icon
  const RefFormattedCell = (cellProps: any) => {
    const row = cellProps.dataItem;

    return (
      <td>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px" }}>
          {props.lockEdit === false && (
            <FaFileInvoice
              style={{
                fontSize: "14pt",
                cursor: "pointer",
                color: row.isSent ? "rgb(30, 42, 78)" : "#e9530d",
              }}
              onClick={() => props.handleQuoteForm(row.quoteId)}
            />
          )}
          <span style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: "5px" }}>
            {row.refFormatted}
            {row.noteCount > 0 && <FaStickyNote />}
          </span>
        </div>
      </td>
    );
  };

  // Custom cell renderer for site column
  const SiteCell = (cellProps: any) => (
    <td>
      <Tooltip anchorElement="target" position="top">
        <span title={cellProps.dataItem.site} className="overflow__container">
          {cellProps.dataItem.site}
        </span>
      </Tooltip>
    </td>
  );

  // Custom cell renderer for description column
  const DescriptionCell = (cellProps: any) => (
    <td>
      <Tooltip anchorElement="target" position="top">
        <span title={cellProps.dataItem.description} className="overflow__container">
          {cellProps.dataItem.description}
        </span>
      </Tooltip>
    </td>
  );

  // Custom cell renderer for cost
  const CostCell = (cellProps: any) => (
    <td>
      £{Number(cellProps.dataItem.cost).toLocaleString()}
    </td>
  );

  // Custom cell renderer for date created
  const DateCreatedCell = (cellProps: any) => (
    <td>
      {moment(cellProps.dataItem.createdDate).format("DD/MM/YYYY")}
    </td>
  );

  // Custom cell renderer for follow-up count with orange or blue circles
  const FollowUpCountCell = (cellProps: any) => {
    const requiresFollowups = cellProps.dataItem.requireFollowups ?? true;
    const count = Math.min(cellProps.dataItem.followUpCount || 0, 3);
    
    return (
      <td>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {requiresFollowups ? (
            // Show orange circles based on followUpCount if requiresFollowups is true
            Array.from({ length: count }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: "#ff8c00",
                }}
              />
            ))
          ) : (
            // Show single blue dot if requiresFollowups is false or unset
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: "rgb(30, 42, 78)",
              }}
            />
          )}
        </div>
      </td>
    );
  };

  return (
    <div className="p-2">
      {/* Header */}
      <div className="grid__title__container" style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
        <div className="grid__header__background">
          <div className="grid__title">
            <div style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "1rem",
              flexWrap: "wrap"
            }}>
              <div>Quotes</div>
              
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "0.5rem", fontSize: "0.9rem" }}>Show Rejected:</label>
                <Switch
                  className="grid__switch"
                  size={"large"}
                  onChange={handleShowRejected}
                  checked={showRejected}
                />
                <Tooltip anchorElement="target" position="top" openDelay={0}>
                  <FaTimesCircle
                    style={{
                      fontSize: "14pt",
                      marginLeft: "0.7rem",
                      color: showRejected ? "red" : "rgba(255, 255, 255, 0.5)",
                    }}
                    title="Show Rejected Quotes"
                  />
                </Tooltip>
              </div>
              
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "0.5rem", fontSize: "0.9rem" }}>Show Accepted:</label>
                <Switch
                  className="grid__switch"
                  size={"large"}
                  onChange={handleShowAccepted}
                  checked={showAccepted}
                />
                <Tooltip anchorElement="target" position="top" openDelay={0}>
                  <FaPlusCircle
                    style={{
                      fontSize: "14pt",
                      marginLeft: "0.7rem",
                      color: showAccepted ? "green" : "rgba(255, 255, 255, 0.5)",
                    }}
                    title="Show Accepted Quotes"
                  />
                </Tooltip>
              </div>

              <select
                style={{
                  marginLeft: 10,
                  height: 40,
                  width: 200,
                  fontSize: "9pt",
                  fontWeight: 400,
                  fontFamily: "Heebo, sans-serif",
                }}
                className="k-dropdownlist"
                onChange={handleMonthCreated}
                value={currentMonth ?? 0}
              >
                {monthSelect.map((month, idx) => (
                  <option key={idx} value={month.value}>{month.text}</option>
                ))}
              </select>
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaCalendarAlt
                  style={{ fontSize: "14pt", marginLeft: "0.5em" }}
                  title="Quote created in"
                />
              </Tooltip>

              <select
                style={{
                  height: 40,
                  width: 200,
                  fontSize: "9pt",
                  fontWeight: 400,
                  fontFamily: "Heebo, sans-serif",
                }}
                className="k-dropdownlist"
                onChange={handleSentFilter}
                value={sentFilter === null ? "" : sentFilter.toString()}
              >
                {sentSelect.map((option, idx) => (
                  <option key={idx} value={option.value}>{option.text}</option>
                ))}
              </select>
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaPaperPlane
                  style={{ fontSize: "14pt", marginLeft: "0.5em" }}
                  title="Quote sent status"
                />
              </Tooltip>

              <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "0.5rem" }}>
                <FormMultiSelect
                  style={{ width: "200px", height: "40px", fontSize: "9pt" }}
                  onChange={handleAddFilteredEmployee}
                  data={employeeData}
                  value={selectedEmployees}
                  placeholder="Filter by Employee..."
                />
                <button
                  style={{
                    border: 0,
                    borderRadius: "0.5em",
                    fontFamily: "Heebo, sans-serif",
                    fontSize: "9pt",
                    width: "100px",
                    height: "40px",
                  }}
                  onClick={handleApplyEmployeeFilter}
                >
                  Filter
                </button>
              </div>
            </div>
       
            <div style={{ display: "flex", flexDirection: "row" }}>
              {props.lockEdit === false && props.onAddToggle && (
                <div onClick={() => props.onAddToggle()} className="grid__addrecord__button">
                  <FaPlusCircle style={{ fontSize: "14pt", marginRight: "1rem" }} />
                  <p style={{ margin: 0 }}>Add new quote</p>
                </div>
              )}

              <div onClick={exportToExcel} className="grid__export__button">
                <FaFileExcel style={{ fontSize: "14pt", marginRight: "1rem" }} />
                <p style={{ margin: 0 }}>Export quotes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-2" />

      {/* Grid Container with Loading Overlay */}
      <div style={{ position: "relative" }}>
        {/* Loading Indicator Overlay */}
        {loading && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            borderRadius: "8px"
          }}>
            <div style={{ textAlign: "center" }}>
              <div className="spinner" />
              <div style={{ marginTop: "1rem", color: "#1e2a4e", fontWeight: 500 }}>
                Loading quotes...
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <Grid
          data={data}
          total={total}
          skip={skip}
          take={take}
          pageable={{
            buttonCount: 5,
            info: true,
            type: 'numeric',
            pageSizes: [10, 20, 50, 100],
            previousNext: true,
          }}
          sortable
          sort={sort}
          onSortChange={handleSortChange}
          filterable
          filter={filter}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          rowRender={rowRender}
          resizable={true}
          style={{ height: 'calc(100vh - 300px)', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}
        >
          {props.lockEdit === false && (
            <GridColumn
              field="quoteDoc"
              title="Quote"
              width="90px"
              filterable={false}
              sortable={false}
              cell={(cellProps: any) => (
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <FaFileInvoice
                      style={{
                        fontSize: "14pt",
                        cursor: "pointer",
                        color: cellProps.dataItem.isSent ? "rgb(30, 42, 78)" : "#e9530d",
                      }}
                      onClick={() => props.handleQuoteForm(cellProps.dataItem.quoteId)}
                    />
                    {/* Emailed from the portal - the latest send, with who it went to. */}
                    {cellProps.dataItem.emailedDate && (
                      <FaEnvelope
                        style={{ fontSize: "12pt", color: "#637b26" }}
                        title={`Emailed ${moment(cellProps.dataItem.emailedDate).format("DD/MM/YYYY HH:mm")}${
                          cellProps.dataItem.emailedTo ? ` to ${cellProps.dataItem.emailedTo}` : ""
                        }`}
                        aria-label="Emailed"
                      />
                    )}
                  </div>
                </td>
              )}
            />
          )}
          <GridColumn
            field="followUpCount"
            title="Follow Ups"
            width="120px"
            cell={FollowUpCountCell}
            filterable={false}
            sortable={false}
          />
          <GridColumn
            field="refFormatted"
            title="Quote Ref"
            width="150px"
            filterCell={QuoteRefFilterCell}
            cell={(cellProps: any) => (
              <td>
                <span style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: "5px" }}>
                  {cellProps.dataItem.refFormatted}
                  {cellProps.dataItem.noteCount > 0 && <FaStickyNote />}
                </span>
              </td>
            )}
          />
          <GridColumn
            field="client"
            title="Client"
            width="200px"
            filterCell={ClientFilterCell}
          />
          <GridColumn
            field="site"
            title="Site"
            width="250px"
            cell={SiteCell}
            filterCell={SiteFilterCell}
          />
          <GridColumn
            field="description"
            title="Description"
            width="250px"
            filterable={true}
          />
          <GridColumn
            field="worktype"
            title="Type of Work"
            width="180px"
            filterCell={WorkTypeFilterCell}
          />
          <GridColumn
            field="cost"
            title="Value"
            width="150px"
            cell={CostCell}
            filterable={false}
          />
          <GridColumn
            field="createdDate"
            title="Date Created"
            width="150px"
            cell={DateCreatedCell}
            filterable={false}
          />
          <GridColumn
            field="assignedTo"
            title="Assigned To"
            width="180px"
            filterCell={AssignedToFilterCell}
          />
        </Grid>
      </div>
    </div>
  );
}

export default QuotesGrid;

